import moment from 'moment';
import { useCallback, useMemo, useRef } from 'react';
import type { RangeValue } from 'rc-picker/lib/interface';
import type { TablePaginationConfig } from 'antd/es/table';
import { listJobs } from 'store/jobs/client';
import { ListPayload } from 'core/http/response';
import { useRequestPromise } from 'hooks/useRequest';
import useJobsParams from 'views/jobs/useJobsParams';
import { GetJobsParams, JobResponse } from 'store/jobs/job';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import type { JobModalFilters } from 'views/jobs/components/JobFiltersModal';

type BuildParams = (applied: GetJobsParams) => GetJobsParams;
type RequestArgs = [GetJobsParams];
type JobsRequest = (params: GetJobsParams) => Promise<ListPayload<JobResponse>>;

const defaultJobsRequest: JobsRequest = (params) => listJobs(params) as Promise<ListPayload<JobResponse>>;

// Allow using different functions for getting jobs
export function useJobsFilters(buildParams: BuildParams, request: JobsRequest = defaultJobsRequest) {
    const buildParamsRef = useRef(buildParams);
    buildParamsRef.current = buildParams;

    const listRequest = useCallback((params: GetJobsParams) => request(params), [request]);

    const {
        queryInputVal,
        appliedParams,
        tags,
        clearAll,
        clearFilter,
        setPage,
        setSize,
        setSortState,
        setExactDate,
        setQueryInputVal,
        setArchived,
        setStatuses,
        setDates,
        setCustomerId,
        setOwnerId,
        setCreatedById,
        setTeamId,
        setIncotermCode,
    } = useJobsParams();

    const {
        page,
        size,
        exactDate,
        sortDirection,
        sort,
        archived,
        jobStatus,
        startDate,
        endDate,
        customerId,
        ownerId,
        createdById,
        teamId,
        incotermCode,
    } = appliedParams;

    const requestParams = useMemo(() => buildParamsRef.current(appliedParams), [appliedParams]);

    const requestOptions = useMemo(() => ({ args: [requestParams] as RequestArgs }), [requestParams]);

    const {
        data,
        isLoading,
        error,
        refetch: _refetch,
    } = useRequestPromise(listRequest, requestOptions, [requestParams]);

    const refetch = useCallback(() => {
        return _refetch(requestParams);
    }, [_refetch, requestParams]);

    const rangeValue: RangeValue<moment.Moment> = useMemo(() => {
        if (!startDate && !endDate) return null;
        return [startDate ? moment(startDate) : null, endDate ? moment(endDate) : null];
    }, [startDate, endDate]);

    const filters: JobModalFilters = useMemo(() => {
        return {
            query: queryInputVal,
            jobStatus,
            range: rangeValue,
            customerId,
            ownerId,
            createdById,
            teamId,
            incotermCode,
        };
    }, [queryInputVal, jobStatus, rangeValue, customerId, ownerId, createdById, teamId, incotermCode]);

    const handleFiltersChange = useCallback(
        (patch: Partial<JobModalFilters>) => {
            if (patch.query !== undefined) {
                setQueryInputVal(patch.query);
                setPage(0);
            }

            if (patch.jobStatus !== undefined) {
                setStatuses(patch.jobStatus);
                setPage(0);
            }

            if (patch.range !== undefined) {
                const [startMoment, endMoment] = patch.range || [];
                setDates({
                    startDate: startMoment ? startMoment.toISOString() : undefined,
                    endDate: endMoment ? endMoment.toISOString() : undefined,
                });
                setPage(0);
            }

            if (patch.customerId !== undefined) {
                setCustomerId(patch.customerId);
                setPage(0);
            }

            if (patch.ownerId !== undefined) {
                setOwnerId(patch.ownerId);
                setPage(0);
            }

            if (patch.createdById !== undefined) {
                setCreatedById(patch.createdById);
                setPage(0);
            }

            if (patch.teamId !== undefined) {
                setTeamId(patch.teamId);
                setPage(0);
            }

            if (patch.incotermCode !== undefined) {
                setIncotermCode(patch.incotermCode);
                setPage(0);
            }
        },
        [
            setQueryInputVal,
            setPage,
            setStatuses,
            setDates,
            setCustomerId,
            setOwnerId,
            setCreatedById,
            setTeamId,
            setIncotermCode,
        ]
    );

    const handleTableChange = useCallback(
        (antPagination: any, _filters: any, sorter: any) => {
            const raw = getTableChangeParams({ pagination: antPagination, sorter });

            const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                page?: number;
                size?: number;
                sortParameter?: string;
                sortDirection?: SortDirection;
            };

            setPage((p) => (typeof page === 'number' ? page : p));
            setSize((p) => (typeof size === 'number' ? size : p));

            if (!sortParameter || !sortDirection) {
                setSortState({ sort: undefined, sortDirection: undefined });
            } else {
                setSortState({
                    sort: sortParameter as any,
                    sortDirection: sortDirection as SortDirections,
                });
            }
        },
        [setPage, setSize, setSortState]
    );

    const handleSearch = useCallback(
        (val: string) => {
            setQueryInputVal(val);
            setPage(0);
        },
        [setQueryInputVal, setPage]
    );

    const handleClearSearch = useCallback(() => {
        setQueryInputVal('');
        setPage(0);
    }, [setQueryInputVal, setPage]);

    const handleExactDateChange = useCallback(
        (val: moment.Moment | null) => {
            if (!val) {
                setExactDate(undefined);
                return;
            }
            setExactDate(val.format('YYYY-MM-DD'));
        },
        [setExactDate]
    );

    const pagination: TablePaginationConfig = useMemo(
        () => ({
            current: page + 1,
            pageSize: size,
            total: data?.total ?? 0,
            showSizeChanger: true,
            hideOnSinglePage: true,
            position: ['bottomCenter'],
        }),
        [page, size, data?.total]
    );

    return {
        data,
        jobs: data?.list ?? [],
        total: data?.total ?? 0,
        isLoading,
        error,
        refetch,

        appliedParams,
        page,
        size,
        exactDate,
        archived,
        sort,
        sortDirection,
        queryInputVal,
        tags,
        filters,
        rangeValue,
        pagination,

        setPage,
        setSize,
        setSortState,
        setQueryInputVal,
        setExactDate,

        clearAll,
        clearFilter,
        setArchived,
        handleFiltersChange,
        handleSearch,
        handleClearSearch,
        handleExactDateChange,
        handleTableChange,
    };
}
